export type ReportContentBlock =
  | { type: 'text'; content: string }
  | { type: 'table'; table: ReportTable };

export interface ReportTable {
  name: string;
  columns: string[];
  rows: Array<Record<string, any>>;
  type?: string;
  /** True when the row JSON could not be parsed or repaired; rows will be empty. */
  parseFailed?: boolean;
  /** Raw fence body preserved for fallback rendering when parseFailed is true. */
  rawBody?: string;
}

export interface ReportSection {
  id: string;
  heading: string;
  microreportId?: string;
  blocks: ReportContentBlock[];
}

export interface ParsedReport {
  title: string;
  subtitle: string;
  sections: ReportSection[];
}

const SECTION_REGEX = /^##\s+(.*)$/gm;
const RENDER_TABLE_REGEX = /```render:table([^\n]*)\n([\s\S]*?)```/gi;

const slugify = (value: string): string => {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'section';
};

const safeJsonParse = <T>(raw: string, fallback: T): T => {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const normalizeKey = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

const sanitizeHeadingText = (value: string): string =>
  (value || '').replace(/\*\*/g, '').trim();

const extractMicroreportMeta = (
  rawHeading: string
): { heading: string; microreportId?: string } => {
  const idMatch = rawHeading.match(/<\s*microreport\s*id:\s*([^>]+?)\s*>/i);
  if (!idMatch) {
    return { heading: sanitizeHeadingText(rawHeading) };
  }
  const microreportId = idMatch[1]?.trim();
  const cleanedHeading = sanitizeHeadingText(rawHeading.replace(idMatch[0], '')).trim();
  return {
    heading: cleanedHeading || sanitizeHeadingText(rawHeading),
    microreportId: microreportId || undefined,
  };
};

const getDaySuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  const mod = day % 10;
  if (mod === 1) return 'st';
  if (mod === 2) return 'nd';
  if (mod === 3) return 'rd';
  return 'th';
};

const formatReportGeneratedSubtitle = (): string => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  const day = now.getUTCDate();
  const month = formatter.format(now);
  const year = now.getUTCFullYear();
  return `Analysis generated on ${day}${getDaySuffix(day)} ${month} ${year}`;
};

const normalizeTableRows = (
  rows: Array<Record<string, any>>,
  columns: string[]
): Array<Record<string, any>> => {
  if (!columns.length) {
    return rows;
  }
  return rows.map((row) => {
    const entries = Object.entries(row).map(([key, value]) => ({
      key,
      value,
      norm: normalizeKey(key),
    }));
    const used = new Set<number>();
    const normalized: Record<string, any> = {};

    columns.forEach((column) => {
      const normCol = normalizeKey(column);
      const exactIdx = entries.findIndex(
        (entry, idx) => !used.has(idx) && entry.norm === normCol
      );
      let matchIdx = exactIdx;
      if (matchIdx === -1) {
        matchIdx = entries.findIndex(
          (entry, idx) =>
            !used.has(idx) &&
            entry.norm.length > 0 &&
            (entry.norm.includes(normCol) || normCol.includes(entry.norm))
        );
      }
      if (matchIdx !== -1) {
        normalized[column] = entries[matchIdx].value;
        used.add(matchIdx);
      } else if (column in row) {
        normalized[column] = (row as Record<string, any>)[column];
      } else {
        normalized[column] = '';
      }
    });

    return normalized;
  });
};

const findOutermostJsonArray = (text: string): string | null => {
  let lastStart = -1;
  for (let i = text.length - 1; i >= 0; i--) {
    if (text[i] === '[') { lastStart = i; break; }
  }
  if (lastStart === -1) return null;

  for (let startIdx = lastStart; startIdx >= 0; startIdx--) {
    if (text[startIdx] !== '[') continue;
    let depth = 0;
    let inString = false;
    let escape = false;
    for (let j = startIdx; j < text.length; j++) {
      const ch = text[j];
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '[') depth++;
      else if (ch === ']') {
        depth--;
        if (depth === 0) {
          const candidate = text.slice(startIdx, j + 1);
          const sanitized = candidate.replace(/\\(?!["\\/bfnrtu])/g, '');
          const parsed = safeJsonParse<unknown>(sanitized, null);
          if (Array.isArray(parsed) && parsed.length > 0 &&
              typeof parsed[0] === 'object' && parsed[0] !== null) {
            return candidate;
          }
          break;
        }
      }
    }
  }
  return null;
};

// --- Bounded repair for malformed render:table row JSON (Known Issue #33) ---
// Model output is untrusted. The most common failure is rows where only the
// first column is keyed and the remaining cells are bare positional strings:
//   {"Scenario":"Baseline","$120,000","$290"}
// These repairs are deterministic (no invented data): positional values are
// mapped, in order, onto the declared columns that don't yet have a value.

const normalizeSmartQuotes = (text: string): string =>
  text.replace(/[\u201C\u201D]/g, '"').replace(/[\u2018\u2019]/g, "'");

const stripTrailingCommas = (text: string): string =>
  text.replace(/,\s*([}\]])/g, '$1');

// Split on top-level commas, ignoring commas inside strings or nested {}/[].
const splitTopLevel = (text: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let inString = false;
  let escape = false;
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (escape) { escape = false; continue; }
    if (ch === '\\') { escape = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (ch === '{' || ch === '[') depth++;
    else if (ch === '}' || ch === ']') depth--;
    else if (ch === ',' && depth === 0) {
      parts.push(text.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(text.slice(start));
  return parts;
};

// Extract balanced top-level {...} slices. Self-synchronizing: each slice walk
// starts fresh at the next '{', so leading garbage (e.g. a truncated first row)
// does not poison the rest of the body.
const extractObjectSlices = (text: string): string[] => {
  const slices: string[] = [];
  let i = 0;
  while (i < text.length) {
    const start = text.indexOf('{', i);
    if (start === -1) break;
    let depth = 0;
    let inString = false;
    let escape = false;
    let end = -1;
    for (let j = start; j < text.length; j++) {
      const ch = text[j];
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) { end = j; break; }
      }
    }
    if (end === -1) { i = start + 1; continue; }
    slices.push(text.slice(start, end + 1));
    i = end + 1;
  }
  return slices;
};

const parseScalar = (raw: string): any | undefined => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

// Repair one row object: keyed pairs are kept; bare positional values fill the
// declared columns that are still missing, in order. Returns null when the row
// doesn't fit a known repairable shape (we never invent data).
const repairRowObject = (
  objectSlice: string,
  columns: string[]
): Record<string, any> | null => {
  const inner = objectSlice.trim().replace(/^\{/, '').replace(/\}$/, '');
  const keyed: Record<string, any> = {};
  const positional: any[] = [];
  for (const item of splitTopLevel(inner)) {
    const trimmed = item.trim();
    if (!trimmed) continue;
    const pairMatch = trimmed.match(/^("(?:[^"\\]|\\.)*")\s*:\s*([\s\S]+)$/);
    if (pairMatch) {
      const key = parseScalar(pairMatch[1]);
      const value = parseScalar(pairMatch[2].trim());
      if (typeof key !== 'string' || value === undefined) return null;
      keyed[key] = value;
      continue;
    }
    const value = parseScalar(trimmed);
    if (value === undefined) return null;
    positional.push(value);
  }
  if (!positional.length) {
    return Object.keys(keyed).length ? keyed : null;
  }
  const missing = columns.filter((column) => !(column in keyed));
  if (positional.length > missing.length) return null;
  positional.forEach((value, idx) => {
    keyed[missing[idx]] = value;
  });
  return keyed;
};

const repairTableRows = (
  body: string,
  columns: string[]
): Array<Record<string, any>> | null => {
  const cleaned = stripTrailingCommas(normalizeSmartQuotes(body));
  const slices = extractObjectSlices(cleaned);
  if (!slices.length) return null;
  const rows: Array<Record<string, any>> = [];
  for (const slice of slices) {
    const row = repairRowObject(slice, columns);
    if (!row) return null;
    rows.push(row);
  }
  return rows.length ? rows : null;
};

const parseRenderTable = (attrs: string, body: string): ReportTable => {
  // Attributes may appear on the same line as ```render:table or on the lines
  // immediately following it. Combine both so we can parse either style.
  const combinedAttrs = [attrs, body].filter(Boolean).join('\n');

  const nameMatch = combinedAttrs.match(/name\s*=\s*"([^"]+)"/i);
  const columnsMatch = combinedAttrs.match(/columns\s*=\s*(\[[^\]]*\])/i);

  const name = nameMatch ? nameMatch[1].trim() : 'Table';
  const columns = columnsMatch ? safeJsonParse<string[]>(columnsMatch[1], []) : [];

  const rowsJson = findOutermostJsonArray(body) || body.trim();

  // Some report generators escape punctuation (e.g., "Scala\-ble") which is not valid JSON.
  // Strip non-JSON escape backslashes before parsing so we don't drop the table.
  const sanitizedRowsJson = rowsJson.replace(/\\(?!["\\/bfnrtu])/g, '');
  const parsedDirect = safeJsonParse<unknown>(sanitizedRowsJson, undefined);

  let rows: Array<Record<string, any>> = [];
  let parseFailed = false;
  let rawBody: string | undefined;

  if (Array.isArray(parsedDirect)) {
    if (parsedDirect.length && Array.isArray(parsedDirect[0]) && columns.length) {
      // Array-of-arrays shape: map values positionally onto declared columns.
      rows = (parsedDirect as any[][]).map((values) => {
        const row: Record<string, any> = {};
        columns.forEach((column, idx) => {
          row[column] = values[idx] ?? '';
        });
        return row;
      });
    } else if (!parsedDirect.length || (typeof parsedDirect[0] === 'object' && parsedDirect[0] !== null && !Array.isArray(parsedDirect[0]))) {
      rows = parsedDirect as Array<Record<string, any>>;
    }
  }

  if (!rows.length && !Array.isArray(parsedDirect)) {
    const repaired = repairTableRows(body, columns);
    if (repaired) {
      rows = repaired;
    } else if (body.trim()) {
      // Fail visible, not silent: renderer shows the raw body as a fallback.
      parseFailed = true;
      rawBody = body.trim();
    }
  }

  return {
    name,
    columns,
    rows: normalizeTableRows(rows, columns),
    type: 'table',
    parseFailed,
    rawBody,
  };
};

export const parseReportMarkdown = (markdown: string): ParsedReport => {
  const normalized = (markdown || '').replace(/\r\n/g, '\n');
  const text = normalized
    .replace(/^###\s+(Macro Interpretation)/gim, '## $1')
    .replace(/^###\s+(Sources|Citations)/gim, '## $1');
  const titleMatch = text.match(/^#\s+(.+)$/m);
  const title = sanitizeHeadingText(titleMatch ? titleMatch[1].trim() : 'AI Analysis');

  const sections: ReportSection[] = [];
  const matches: Array<{ heading: string; start: number; bodyStart: number }> =
    [];

  let match: RegExpExecArray | null;
  while ((match = SECTION_REGEX.exec(text)) !== null) {
    matches.push({
      heading: match[1].trim(),
      start: match.index,
      bodyStart: match.index + match[0].length,
    });
  }

  const contentLength = text.length;
  if (matches.length === 0) {
    const body = text.replace(/^#.*$/m, '').trim();
    const blocks: ReportContentBlock[] = [];
    const pushText = (chunk: string) => {
      const trimmed = chunk.trim();
      if (trimmed.length > 0) {
        blocks.push({ type: 'text', content: trimmed });
      }
    };
    let cursor = 0;
    let tableMatch: RegExpExecArray | null;
    RENDER_TABLE_REGEX.lastIndex = 0;
    while ((tableMatch = RENDER_TABLE_REGEX.exec(body)) !== null) {
      pushText(body.slice(cursor, tableMatch.index));
      blocks.push({ type: 'table', table: parseRenderTable(tableMatch[1] || '', tableMatch[2] || '[]') });
      cursor = tableMatch.index + tableMatch[0].length;
    }
    pushText(body.slice(cursor));
    if (!blocks.length) {
      blocks.push({ type: 'text', content: '' });
    }
    sections.push({
      id: 'report',
      heading: 'Analysis Content',
      blocks,
    });
  } else {
    // Capture any introductory text before the first section heading so it renders (e.g., Insights)
    const preambleEnd = matches[0].start;
    const preamble = text
      .slice(0, preambleEnd)
      .replace(/^#.*$/m, '')
      .trim();
    if (preamble.length > 0) {
      sections.push({
        id: 'summary',
        heading: 'Key Insights Summary',
        blocks: [{ type: 'text', content: preamble }],
      });
    }

    matches.forEach((sectionMatch, idx) => {
      const next = matches[idx + 1];
      const body = text
        .slice(sectionMatch.bodyStart, next ? next.start : contentLength)
        .trim();

      const blocks: ReportContentBlock[] = [];
      const pushText = (chunk: string) => {
        const trimmed = chunk.trim();
        if (trimmed.length > 0) {
          blocks.push({ type: 'text', content: trimmed });
        }
      };
      let cursor = 0;
      let tableMatch: RegExpExecArray | null;
      RENDER_TABLE_REGEX.lastIndex = 0;

      while ((tableMatch = RENDER_TABLE_REGEX.exec(body)) !== null) {
        pushText(body.slice(cursor, tableMatch.index));
        blocks.push({ type: 'table', table: parseRenderTable(tableMatch[1] || '', tableMatch[2] || '[]') });
        cursor = tableMatch.index + tableMatch[0].length;
      }
      pushText(body.slice(cursor));
      if (!blocks.length) {
        blocks.push({ type: 'text', content: '' });
      }

      const heading = sanitizeHeadingText(sectionMatch.heading);
      const { heading: displayHeading, microreportId } = extractMicroreportMeta(
        sectionMatch.heading
      );
      sections.push({
        id: slugify(displayHeading || heading),
        heading: displayHeading || heading,
        microreportId,
        blocks,
      });
    });
  }

  return {
    title,
    subtitle: formatReportGeneratedSubtitle(),
    sections,
  };
};

